export type PunchStanceId = 'rest' | 'guard';
export type PunchClipId = 'jabR' | 'jabL' | 'uppercut' | 'liver';
export type PunchKeyId = 'init' | 'pos1' | 'pos2' | 'end';

export interface FistXform {
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
}

export interface PunchKey {
  id: PunchKeyId;
  label: string;
  /** 0..1 of clip duration. */
  t: number;
  right: FistXform;
  left: FistXform;
}

export interface PunchClip {
  id: PunchClipId;
  label: string;
  duration: number;
  keys: PunchKey[];
}

export const PUNCH_CLIP_ORDER: PunchClipId[] = ['jabR', 'jabL', 'uppercut', 'liver'];
export const PUNCH_KEY_ORDER: PunchKeyId[] = ['init', 'pos1', 'pos2', 'end'];
export const PUNCH_CLIP_LABELS: Record<PunchClipId, string> = {
  jabR: 'Jab R',
  jabL: 'Jab L',
  uppercut: 'Uppercut',
  liver: 'Liver',
};
export const PUNCH_KEY_LABELS: Record<PunchKeyId, string> = {
  init: 'Init',
  pos1: 'Pos 1',
  pos2: 'Pos 2',
  end: 'End',
};

export function fist(x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): FistXform {
  return { x, y, z, rx, ry, rz };
}

export function cloneFist(f: FistXform): FistXform {
  return { ...f };
}

export function lerpFist(a: FistXform, b: FistXform, t: number): FistXform {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
    rx: a.rx + (b.rx - a.rx) * t,
    ry: a.ry + (b.ry - a.ry) * t,
    rz: a.rz + (b.rz - a.rz) * t,
  };
}

export function addFist(a: FistXform, d: FistXform): FistXform {
  return {
    x: a.x + d.x,
    y: a.y + d.y,
    z: a.z + d.z,
    rx: a.rx + d.rx,
    ry: a.ry + d.ry,
    rz: a.rz + d.rz,
  };
}

export function scaleFist(f: FistXform, s: number): FistXform {
  return {
    x: f.x * s,
    y: f.y * s,
    z: f.z * s,
    rx: f.rx * s,
    ry: f.ry * s,
    rz: f.rz * s,
  };
}

export function subFist(a: FistXform, b: FistXform): FistXform {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z,
    rx: a.rx - b.rx,
    ry: a.ry - b.ry,
    rz: a.rz - b.rz,
  };
}

const REST_R = fist(0.16, -0.28, -0.3, 0.35, 0.15, 0.2);
const REST_L = fist(-0.16, -0.3, -0.28, 0.38, -0.12, -0.18);
const GUARD_R = fist(0.13, -0.02, -0.4, 0.15, 0.08, 0.12);
const GUARD_L = fist(-0.12, -0.04, -0.38, 0.18, -0.06, -0.1);

const STANCES: Record<PunchStanceId, { right: FistXform; left: FistXform }> = {
  rest: { right: REST_R, left: REST_L },
  guard: { right: GUARD_R, left: GUARD_L },
};

function key(id: PunchKeyId, t: number, right: FistXform, left: FistXform): PunchKey {
  return { id, label: PUNCH_KEY_LABELS[id], t, right: cloneFist(right), left: cloneFist(left) };
}

function clip(
  id: PunchClipId,
  duration: number,
  pos1T: number,
  pos1R: FistXform,
  pos1L: FistXform,
  pos2T: number,
  pos2R: FistXform,
  pos2L: FistXform,
  endT = 1,
): PunchClip {
  return {
    id,
    label: PUNCH_CLIP_LABELS[id],
    duration,
    keys: [
      key('init', 0, REST_R, REST_L),
      key('pos1', pos1T, pos1R, pos1L),
      key('pos2', pos2T, pos2R, pos2L),
      key('end', endT, REST_R, REST_L),
    ],
  };
}

const CLIPS: Record<PunchClipId, PunchClip> = {
  jabR: clip(
    'jabR',
    0.28,
    0.13,
    fist(0.191, -0.292, -0.5, 0.2, 0.095, 0.14),
    fist(-0.15, -0.301, -0.31, 0.315, -0.1, -0.15),
    0.44,
    fist(0.209, -0.301, -0.7, -0.93, -0.25, -0.14),
    fist(-0.15, -0.301, -0.327, 0.315, -0.1, -0.15),
    0.601,
  ),
  jabL: clip(
    'jabL',
    0.28,
    0.13,
    fist(0.16, -0.28, -0.3, 0.35, 0.15, 0.15),
    fist(-0.182, -0.41, -0.455, -0.79, 0.04, 0.32),
    0.44,
    fist(0.16, -0.28, -0.3, 0.35, 0.15, 0.1),
    fist(-0.228, -0.383, -0.937, -1, 0.25, 0.25),
    0.601,
  ),
  uppercut: clip(
    'uppercut',
    0.36,
    0.24,
    fist(0.155, -0.383, -0.692, 0.07, 0.14, -0.25),
    fist(-0.155, -0.301, -0.373, 0.34, -0.1, -0.15),
    0.55,
    fist(0.118, 0.164, -0.81, 0.11, 0.36, -0.29),
    fist(-0.155, -0.191, -0.391, 0.29, 0.04, -0.12),
  ),
  liver: clip(
    'liver',
    0.34,
    0.2,
    fist(0.428, -0.264, -0.537, 0.11, -0.86, 1.18),
    fist(-0.182, -0.24, -0.427, 0.415, -0.12, -0.19),
    0.52,
    fist(-0.228, -0.319, -0.974, -0.32, 0.07, 1.18),
    fist(-0.191, -0.246, -0.509, 0.45, -0.12, -0.2),
  ),
};

export function bakedStance(id: PunchStanceId): { right: FistXform; left: FistXform } {
  const s = STANCES[id];
  return { right: cloneFist(s.right), left: cloneFist(s.left) };
}

export function bakedPunchClip(id: PunchClipId): PunchClip {
  return clonePunchClip(CLIPS[id]);
}

export function bakedPunchClips(): Record<PunchClipId, PunchClip> {
  return {
    jabR: bakedPunchClip('jabR'),
    jabL: bakedPunchClip('jabL'),
    uppercut: bakedPunchClip('uppercut'),
    liver: bakedPunchClip('liver'),
  };
}

export function clonePunchKey(k: PunchKey): PunchKey {
  return { ...k, right: cloneFist(k.right), left: cloneFist(k.left) };
}

export function clonePunchClip(src: PunchClip): PunchClip {
  return {
    id: src.id,
    label: src.label,
    duration: src.duration,
    keys: src.keys.map(clonePunchKey),
  };
}

export function clonePunchClips(): Record<PunchClipId, PunchClip> {
  return bakedPunchClips();
}

export function copyPunchKey(dst: PunchKey, src: PunchKey): void {
  dst.t = src.t;
  dst.right = cloneFist(src.right);
  dst.left = cloneFist(src.left);
}

export function punchKeyOf(clip: PunchClip, id: PunchKeyId): PunchKey | null {
  return clip.keys.find((k) => k.id === id) ?? null;
}

export function punchImpactT(clip: PunchClip): number {
  return punchKeyOf(clip, 'pos2')?.t ?? 0.55;
}

/** Hip rest → guard, blended by ADS 0..1 (same damp as guns). */
export function blendStance(ads: number): { right: FistXform; left: FistXform } {
  const t = Math.min(1, Math.max(0, ads));
  return {
    right: lerpFist(STANCES.rest.right, STANCES.guard.right, t),
    left: lerpFist(STANCES.rest.left, STANCES.guard.left, t),
  };
}

export function stanceOffset(ads: number): { right: FistXform; left: FistXform } {
  const t = Math.min(1, Math.max(0, ads));
  if (t <= 0) return { right: fist(0, 0, 0), left: fist(0, 0, 0) };
  return {
    right: scaleFist(subFist(STANCES.guard.right, STANCES.rest.right), t),
    left: scaleFist(subFist(STANCES.guard.left, STANCES.rest.left), t),
  };
}

export function applyStance(fists: { right: FistXform; left: FistXform }, ads: number): {
  right: FistXform;
  left: FistXform;
} {
  const o = stanceOffset(ads);
  return { right: addFist(fists.right, o.right), left: addFist(fists.left, o.left) };
}

export function samplePunchClip(clip: PunchClip, u: number): { right: FistXform; left: FistXform } {
  const keys = clip.keys;
  if (keys.length === 0) return bakedStance('rest');
  const t = Math.min(1, Math.max(0, u));
  if (t <= keys[0]!.t) return { right: cloneFist(keys[0]!.right), left: cloneFist(keys[0]!.left) };
  const last = keys[keys.length - 1]!;
  if (t >= last.t) return { right: cloneFist(last.right), left: cloneFist(last.left) };
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i]!;
    const b = keys[i + 1]!;
    if (t > b.t) continue;
    const span = Math.max(1e-4, b.t - a.t);
    const k = (t - a.t) / span;
    const ease = k * k * (3 - 2 * k);
    return { right: lerpFist(a.right, b.right, ease), left: lerpFist(a.left, b.left, ease) };
  }
  return { right: cloneFist(last.right), left: cloneFist(last.left) };
}

export const PUNCH_COMBO: PunchClipId[] = ['jabL', 'jabR', 'jabL', 'liver', 'uppercut'];
/** Live playback vs authored clip duration. */
export const PUNCH_PLAY_SPEED = 1.4;
/** Seconds after a punch before the combo resets to jab L. */
export const PUNCH_COMBO_WINDOW = 0.7;

export function nextPunchCombo(step: number): { id: PunchClipId; nextStep: number } {
  const n = PUNCH_COMBO.length;
  const i = ((step % n) + n) % n;
  return { id: PUNCH_COMBO[i]!, nextStep: (i + 1) % n };
}

export function punchPlayDuration(clip: PunchClip): number {
  return Math.max(0.05, clip.duration / PUNCH_PLAY_SPEED);
}

/** Jab smallest; liver heavier; uppercut hardest. Still enough to drop a brute in a short combo. */
export const PUNCH_DAMAGE: Record<PunchClipId, number> = {
  jabR: 16,
  jabL: 16,
  liver: 26,
  uppercut: 34,
};

export const PUNCH_SWING_RATE: Record<PunchClipId, number> = {
  jabR: 1.2,
  jabL: 1.04,
  liver: 0.84,
  uppercut: 0.7,
};

export const PUNCH_STUN: Record<PunchClipId, number> = {
  jabR: 0.42,
  jabL: 0.42,
  liver: 0.55,
  uppercut: 0.72,
};

export function punchDamage(id: PunchClipId): number {
  return PUNCH_DAMAGE[id];
}

export function punchSwingRate(id: PunchClipId): number {
  return PUNCH_SWING_RATE[id];
}

export function punchStun(id: PunchClipId): number {
  return PUNCH_STUN[id];
}
